import { useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Globe,
  ImagePlus,
  Loader2,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Input } from '../../components/Input';
import { Stepper } from '../../components/Stepper';
import { Badge } from '../../components/Badge';
import { cn } from '../../lib/cn';
import { useNavigate } from '@tanstack/react-router';
import { updateReseller } from '../../api/reseller';

const STEPS = ['Organization', 'Branding', 'Domain', 'Support', 'Invite staff'];

type StaffRow = { id: number; email: string; role: 'admin' | 'staff' | 'readonly' };

/** 5-step reseller onboarding wizard with internal step state. */
export function ResellerOnboarding() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Step 1
  const [orgName, setOrgName] = useState('Acme Resellers');
  const [displayName, setDisplayName] = useState('Acme POS');
  const [address, setAddress] = useState('12 Main Rd, Sylhet');
  const [phone, setPhone] = useState('+8801XXXXXXX');
  const [website, setWebsite] = useState('https://acme-resellers.com');

  // Step 2
  const [primaryColor, setPrimaryColor] = useState('#0F766E');

  // Step 3
  const [customDomain, setCustomDomain] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);

  // Step 4
  const [supportEmail, setSupportEmail] = useState('help@acme-resellers.com');
  const [supportPhone, setSupportPhone] = useState('+8801XXXXXXX');
  const [office, setOffice] = useState('12 Main Rd, Sylhet');
  const [allowCash, setAllowCash] = useState(true);

  // Step 5
  const [staff, setStaff] = useState<StaffRow[]>([
    { id: 1, email: '', role: 'admin' },
    { id: 2, email: '', role: 'staff' },
  ]);

  async function next() {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
      return;
    }
    // Last step → persist the org profile + branding, then enter the dashboard.
    setSubmitting(true);
    setError(null);
    try {
      await updateReseller({
        name: orgName.trim(),
        senderDomain: customDomain.trim() || undefined,
        branding: { displayName, primaryColor, address, phone, website, supportEmail, supportPhone, office, allowCash },
      });
      navigate({ to: '/reseller' });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save your setup. Please try again.');
      setSubmitting(false);
    }
  }
  function back() {
    if (step > 0) setStep(step - 1);
  }

  function verifyDns() {
    setVerifying(true);
    setTimeout(() => {
      setVerifying(false);
      setVerified(true);
    }, 1400);
  }

  function addStaffRow() {
    setStaff((prev) => [...prev, { id: Date.now(), email: '', role: 'staff' }]);
  }
  function removeStaffRow(id: number) {
    setStaff((prev) => prev.filter((s) => s.id !== id));
  }

  return (
    <div className="min-h-screen bg-marketing dark:bg-marketing-dark py-8 px-4 sm:py-12">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8 text-center">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            Welcome to Possignin
          </h1>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
            Set up your reseller account in five quick steps.
          </p>
        </div>

        <Card className="p-6 sm:p-8">
          <div className="mb-6 sm:mb-8">
            <Stepper steps={STEPS} current={step} />
          </div>

          {step === 0 && (
            <div className="space-y-5">
              <Input label="Organization name" required value={orgName} onChange={(e) => setOrgName(e.target.value)} />
              <Input
                label="Display name"
                hint="How customers see your brand in the POS UI"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
              <Input label="Address" value={address} onChange={(e) => setAddress(e.target.value)} />
              <div className="grid sm:grid-cols-2 gap-5">
                <Input label="Contact phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
                <Input label="Website" value={website} onChange={(e) => setWebsite(e.target.value)} />
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-5">
                <UploadField label="Logo" hint="SVG or PNG, ≤ 1 MB" />
                <UploadField label="Favicon" hint="PNG or ICO, square 32×32" />
              </div>

              <div className="grid sm:grid-cols-[1fr_auto] gap-4 items-end">
                <Input
                  label="Primary color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  hint="Used for buttons, links, accents on your reseller pages"
                />
                <label className="flex flex-col items-start gap-1.5">
                  <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Picker</span>
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="w-12 h-10 rounded-md border border-zinc-300 dark:border-zinc-700 cursor-pointer bg-transparent"
                  />
                </label>
              </div>

              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-2">
                  Live preview
                </div>
                <div
                  className="rounded-lg border-2 p-5 bg-white dark:bg-zinc-900"
                  style={{ borderColor: primaryColor }}
                >
                  <div className="flex items-center gap-2.5 mb-4">
                    <span
                      className="w-7 h-7 rounded-md grid place-items-center text-white font-semibold text-xs"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {displayName.charAt(0) || 'A'}
                    </span>
                    <span className="font-semibold text-sm text-zinc-950 dark:text-zinc-50">{displayName}</span>
                    <span className="ml-auto text-xs text-zinc-500 dark:text-zinc-400">
                      POS for shops in Bangladesh
                    </span>
                  </div>
                  <button
                    type="button"
                    className="px-4 py-2 rounded-md text-white text-sm font-medium"
                    style={{ backgroundColor: primaryColor }}
                  >
                    Start your free trial
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div>
                <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Default subdomain</div>
                <div className="flex items-center gap-3 p-3 rounded-md bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700">
                  <Globe className="w-4 h-4 text-zinc-500 dark:text-zinc-400" />
                  <span className="font-medium text-zinc-950 dark:text-zinc-50 text-sm">acme.itcitysoft.com</span>
                  <Badge tone="success">Already yours</Badge>
                </div>
              </div>

              <div>
                <Input
                  label="Custom domain"
                  hint="Optional — point a domain you already own"
                  placeholder="pos.acme-resellers.com"
                  value={customDomain}
                  onChange={(e) => {
                    setCustomDomain(e.target.value);
                    setVerified(false);
                  }}
                />
                {customDomain && (
                  <div className="mt-3 p-4 rounded-md bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 text-sm">
                    <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-2">
                      Add this DNS record
                    </div>
                    <div className="font-mono text-xs text-zinc-700 dark:text-zinc-200">
                      <div>Type: CNAME</div>
                      <div>Name: {customDomain.split('.')[0]}</div>
                      <div>Value: cname.itcitysoft.com</div>
                    </div>
                    <div className="mt-3 flex items-center gap-3">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={verifyDns}
                        disabled={verifying || verified}
                        iconLeft={verifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : undefined}
                      >
                        {verifying ? 'Checking…' : verified ? 'Verified' : 'Verify DNS'}
                      </Button>
                      {verified ? (
                        <Badge tone="success">Verified</Badge>
                      ) : verifying ? (
                        <Badge tone="warning">Pending… auto-checking every 30s</Badge>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <Input label="Support email" required type="email" value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)} />
              <Input label="Support phone" required value={supportPhone} onChange={(e) => setSupportPhone(e.target.value)} />
              <Input label="Office address" value={office} onChange={(e) => setOffice(e.target.value)} />

              <label className="flex items-start gap-3 p-3 rounded-md bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allowCash}
                  onChange={(e) => setAllowCash(e.target.checked)}
                  className="mt-0.5 rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-zinc-950 dark:text-zinc-50">
                    Allow cash payments at office
                  </div>
                  <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                    Admins can record cash receipts manually for walk-in customers.
                  </p>
                </div>
              </label>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <p className="text-sm text-zinc-600 dark:text-zinc-300">
                Invite up to 5 teammates now (you can add more later).
              </p>
              <div className="space-y-3">
                {staff.map((row, idx) => (
                  <div key={row.id} className="grid grid-cols-[1fr_140px_auto] gap-2 sm:gap-3 items-start">
                    <Input
                      placeholder={`staff${idx + 1}@acme-resellers.com`}
                      value={row.email}
                      onChange={(e) =>
                        setStaff((prev) => prev.map((s) => (s.id === row.id ? { ...s, email: e.target.value } : s)))
                      }
                    />
                    <select
                      value={row.role}
                      onChange={(e) =>
                        setStaff((prev) =>
                          prev.map((s) =>
                            s.id === row.id ? { ...s, role: e.target.value as StaffRow['role'] } : s,
                          ),
                        )
                      }
                      className="px-3 py-2.5 rounded-md text-sm bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand dark:focus:ring-brand-dm"
                    >
                      <option value="admin">Admin</option>
                      <option value="staff">Staff</option>
                      <option value="readonly">Read-only</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => removeStaffRow(row.id)}
                      disabled={staff.length === 1}
                      className="w-10 h-10 grid place-items-center rounded-md text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-30 transition"
                      aria-label="Remove row"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addStaffRow}
                  disabled={staff.length >= 5}
                  className="inline-flex items-center gap-2 text-sm font-medium text-brand dark:text-brand-dm hover:underline disabled:opacity-50"
                >
                  <Plus className="w-4 h-4" />
                  Add another
                </button>
              </div>
            </div>
          )}

          {error && (
            <p className="mt-6 text-sm text-rose-600 dark:text-rose-400" role="alert">
              {error}
            </p>
          )}

          {/* Footer */}
          <div className="mt-8 pt-5 border-t border-zinc-200 dark:border-zinc-700 flex items-center justify-between gap-3">
            {step === 0 ? (
              <Button variant="ghost" type="button" onClick={() => navigate({ to: '/reseller' })}>
                Skip for now
              </Button>
            ) : (
              <Button variant="ghost" type="button" onClick={back} disabled={submitting} iconLeft={<ArrowLeft className="w-4 h-4" />}>
                Back
              </Button>
            )}
            <Button onClick={next} loading={submitting} disabled={submitting} iconRight={!submitting ? <ArrowRight className="w-4 h-4" /> : undefined}>
              {step === STEPS.length - 1 ? 'Finish setup' : 'Continue'}
            </Button>
          </div>
        </Card>

        <p className="mt-6 text-center text-xs text-zinc-500 dark:text-zinc-400">
          Setup takes about 5 minutes. You can always edit these later in Settings.
        </p>
      </div>
    </div>
  );
}

function UploadField({ label, hint }: { label: string; hint: string }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">{label}</span>
      <div
        className={cn(
          'flex flex-col items-center justify-center gap-2 px-4 py-6 rounded-md border-2 border-dashed cursor-pointer transition',
          'border-zinc-300 dark:border-zinc-700 hover:border-zinc-400 dark:hover:border-zinc-600',
          'bg-zinc-50/50 dark:bg-zinc-900/40',
        )}
      >
        <span className="w-10 h-10 grid place-items-center rounded-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400">
          <ImagePlus className="w-4 h-4" />
        </span>
        <span className="text-sm font-medium text-zinc-700 dark:text-zinc-200 inline-flex items-center gap-1.5">
          <Upload className="w-3.5 h-3.5" />
          Click to upload
        </span>
        <span className="text-[11px] text-zinc-500 dark:text-zinc-400">{hint}</span>
        <input type="file" className="sr-only" />
      </div>
    </label>
  );
}

// Suppress unused-warning for CheckCircle which is reserved for the verified state badge variant
void CheckCircle;
